import type { PublicAccount } from "../api/types";

const ACCOUNT_KEY = "hw.auth.lastAccount.v1";

function isDemoExpired(account: PublicAccount, nowMs: number = Date.now()): boolean {
  if (!account.isDemo || account.demoExpiresAt === null) {
    return false;
  }
  const expiresAt = Date.parse(account.demoExpiresAt);
  return Number.isNaN(expiresAt) || expiresAt <= nowMs;
}

function normalizeAccount(record: Record<string, unknown>): PublicAccount | null {
  if (
    typeof record.accountId !== "string" ||
    typeof record.username !== "string" ||
    typeof record.displayName !== "string" ||
    typeof record.collectionRevision !== "number"
  ) {
    return null;
  }
  const isDemo = record.isDemo === true;
  const demoExpiresAt =
    record.demoExpiresAt === null
      ? null
      : typeof record.demoExpiresAt === "string"
        ? record.demoExpiresAt
        : null;
  return {
    accountId: record.accountId,
    username: record.username,
    displayName: record.displayName,
    collectionRevision: record.collectionRevision,
    isDemo,
    demoExpiresAt,
  };
}

/**
 * Non-secret last-known account for offline shell bootstrap.
 * Cleared on logout. Never stores passwords or session tokens.
 */
export function saveLastAccount(account: PublicAccount, storage: Storage = localStorage): void {
  storage.setItem(
    ACCOUNT_KEY,
    JSON.stringify({
      accountId: account.accountId,
      username: account.username,
      displayName: account.displayName,
      collectionRevision: account.collectionRevision,
      isDemo: account.isDemo,
      demoExpiresAt: account.demoExpiresAt,
    }),
  );
}

export function loadLastAccount(storage: Storage = localStorage): PublicAccount | null {
  const raw = storage.getItem(ACCOUNT_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    const account = normalizeAccount(parsed as Record<string, unknown>);
    if (!account) {
      return null;
    }
    if (isDemoExpired(account)) {
      clearLastAccount(storage);
      return null;
    }
    return account;
  } catch {
    return null;
  }
}

export function clearLastAccount(storage: Storage = localStorage): void {
  storage.removeItem(ACCOUNT_KEY);
}

export { isDemoExpired };
